import { MB_HOURS_PER_GB_MONTH } from "./MeterInfo";
import { PRICING_CONSTANTS } from "./PricingAssumptions";
import { DEFAULT_REPO_PRICES, getMeterProductKey, type RepoPrices } from "./PricingPrices";
import { ENABLE_PEOPLE_AND_FACES } from "./SharedConstants";
import {
  getTextClassificationBillableTokens,
  MINIMUM_TEXT_CLASSIFICATION_BILLABLE_TOKENS,
} from "./Subscriptions";
import { calculateTieredPricing } from "./TieredPricingUtils";
import type { UsageMeterEventName } from "./UsageMeter";

/**
 * Public price list and monthly cost estimate, derived from the repo copy of the Stripe prices
 * (PricingPrices.json). Rates use the same conversion as the billing usage report (Stripe minor
 * units / 100) and the same graduated tier calculation (calculateTieredPricing), so the published
 * numbers cannot drift from the prices the checkout subscribes customers to.
 */

export interface MeterRateTier {
  /** Inclusive upper bound of the tier in raw meter units; null for the last tier. */
  upTo: number | null;
  /** Price per raw meter unit in major currency units (e.g. pounds). */
  rate: number;
}

const PEOPLE_AND_FACES_METERS: readonly UsageMeterEventName[] = [
  "person_detection",
  "face_detection",
];
/** Meters shown in the public price list, in display order. Disabled features are left out. */
export const PUBLIC_PRICED_METERS = (
  [
    "image_classification",
    "text_classification_tokens",
    "ocr_classification",
    "person_detection",
    "face_detection",
    "asset_storage_mb_hours",
    "asset_search",
    "asset_media_search_indexing",
    "asset_read",
    "image_transformed",
    "website_ingestion",
  ] as const satisfies readonly UsageMeterEventName[]
).filter((meter) => ENABLE_PEOPLE_AND_FACES || !PEOPLE_AND_FACES_METERS.includes(meter));

/** Quantity each public unit price is quoted for, e.g. "per 1,000 images". */
export const PUBLIC_PRICE_QUANTITY: Record<UsageMeterEventName, number> = {
  image_classification: 1_000,
  text_classification_tokens: 1_000_000,
  ocr_classification: 1_000,
  person_detection: 1_000,
  face_detection: 1_000,
  asset_storage_mb_hours: MB_HOURS_PER_GB_MONTH,
  asset_search: 1_000,
  asset_media_search_indexing: 1_000,
  asset_read: 1_000_000,
  image_transformed: 1_000,
  website_ingestion: 1_000,
};

/** Upper-case ISO currency code shared by every usage price (throws if prices are mixed). */
export const getBillingCurrency = (prices: RepoPrices = DEFAULT_REPO_PRICES): string => {
  const currencies = new Set(
    Object.values(prices).map((price) => price?.currency.toUpperCase() ?? ""),
  );
  currencies.delete("");
  if (currencies.size !== 1) {
    throw new Error(`Expected one billing currency, found: ${[...currencies].join(", ")}`);
  }
  return [...currencies][0];
};

export const BILLING_CURRENCY = getBillingCurrency();

/** Rate tiers for a usage meter in major currency units per raw meter unit. */
export const getMeterRateTiers = (
  meter: UsageMeterEventName,
  prices: RepoPrices = DEFAULT_REPO_PRICES,
): MeterRateTier[] => {
  const price = prices[getMeterProductKey(meter)];
  if (!price) throw new Error(`No price for meter: ${meter}`);
  if (price.tiers) {
    return price.tiers.map((tier) => ({
      upTo: tier.upTo,
      rate: Number(tier.unitAmountDecimal ?? 0) / 100,
    }));
  }
  return [{ upTo: null, rate: Number(price.unitAmountDecimal ?? 0) / 100 }];
};

export interface MeterPriceSummary {
  meter: UsageMeterEventName;
  /** Units included free each month before the paid rate applies (0 when none). */
  freeAllowance: number;
  /** Paid (marginal) rate per raw meter unit, in major currency units. */
  unitRate: number;
  /** Quantity the public price is quoted for (see PUBLIC_PRICE_QUANTITY). */
  quotedQuantity: number;
  /** Price for quotedQuantity paid units, in major currency units. */
  quotedPrice: number;
}

/**
 * Summarises a meter's price as a free allowance followed by a paid rate. CalmLens tiers are
 * always "free up to N, then a single rate"; anything else is rejected rather than misreported.
 */
export const getMeterPriceSummary = (
  meter: UsageMeterEventName,
  prices: RepoPrices = DEFAULT_REPO_PRICES,
): MeterPriceSummary => {
  const tiers = getMeterRateTiers(meter, prices);
  const paidTier = tiers[tiers.length - 1];
  const freeTiers = tiers.slice(0, -1);
  if (freeTiers.some((tier) => tier.rate !== 0)) {
    throw new Error(`Meter ${meter} has a paid tier before its last tier`);
  }
  const freeAllowance = freeTiers.reduce((max, tier) => Math.max(max, tier.upTo ?? 0), 0);
  const quotedQuantity = PUBLIC_PRICE_QUANTITY[meter];
  return {
    meter,
    freeAllowance,
    unitRate: paidTier.rate,
    quotedQuantity,
    quotedPrice: paidTier.rate * quotedQuantity,
  };
};

export const getPublicPriceList = (prices: RepoPrices = DEFAULT_REPO_PRICES) =>
  PUBLIC_PRICED_METERS.map((meter) => getMeterPriceSummary(meter, prices));

/** Cost of one text check at or below the per-request billing minimum. */
export const getMinimumTextCheckCost = (prices: RepoPrices = DEFAULT_REPO_PRICES) =>
  calculateTieredPricing(
    MINIMUM_TEXT_CLASSIFICATION_BILLABLE_TOKENS,
    getMeterRateTiers("text_classification_tokens", prices),
  ).totalCost;

/** Planning approximation for converting characters to tokens (PricingSettings.json). */
export const CHARACTERS_PER_TOKEN = PRICING_CONSTANTS.charactersPerToken;

export const estimateTokensFromCharacters = (characters: number) =>
  Math.ceil(Math.max(0, characters) / CHARACTERS_PER_TOKEN);

export interface MonthlyUsageInput {
  /** Images uploaded for moderation each month. */
  imagesPerMonth: number;
  /** Successful natural-language asset searches; keyword search is free. */
  assetSearchesPerMonth?: number;
  /** One unit per started six seconds of audio/video indexed in projects with media search on. */
  mediaSearchIndexingUnitsPerMonth?: number;
  /** How many of those images have OCR enabled (capped at imagesPerMonth). */
  ocrImagesPerMonth: number;
  /** How many of those images have person detection on (preview; capped at imagesPerMonth). */
  personDetectionImagesPerMonth?: number;
  /** How many of those images have face detection on (preview; capped at imagesPerMonth). */
  faceDetectionImagesPerMonth?: number;
  /** Text items (comments, captions, bios...) uploaded for moderation each month. */
  textItemsPerMonth: number;
  /** Average tokens per text item; the per-request billing minimum is applied on top. */
  averageTextTokens: number;
  /** Gigabytes of retained content at the end of the month. */
  storedGb: number;
  /** CDN reads (asset deliveries) each month. */
  cdnReadsPerMonth: number;
  /** Unique image variants first used in billable requests this month; exclude reused variants. */
  imageTransformsPerMonth: number;
}

export interface MonthlyCostLine {
  meter: UsageMeterEventName;
  /** Raw meter quantity for the month (tokens for text, GB for storage). */
  quantity: number;
  /** Portion of quantity covered by the free allowance. */
  freeQuantity: number;
  cost: number;
}

export interface MonthlyCostEstimate {
  currency: string;
  lines: MonthlyCostLine[];
  total: number;
  /** Text tokens billed for text items, after the per-request minimum. */
  textItemTokens: number;
  /** Text tokens billed for OCR text, assuming every OCR image contains short text. */
  ocrTextTokens: number;
}

const nonNegative = (value: number) => (Number.isFinite(value) ? Math.max(0, value) : 0);

/**
 * Estimates a month's usage-based bill. Mirrors how the backend meters usage:
 * - each image records one image classification, plus one OCR classification when OCR is on and
 *   one person or face detection when those features are on;
 * - each text check records its tokens with a 250-token minimum;
 * - OCR text found in an image is classified as text and metered the same way (assumed here to
 *   be short text, billed at the minimum);
 * - storage is the gigabytes retained all month, in megabyte-hours.
 */
export const estimateMonthlyCost = (
  input: MonthlyUsageInput,
  prices: RepoPrices = DEFAULT_REPO_PRICES,
): MonthlyCostEstimate => {
  const images = Math.floor(nonNegative(input.imagesPerMonth));
  const ocrImages = Math.min(images, Math.floor(nonNegative(input.ocrImagesPerMonth)));
  const featureImages = (value: number | undefined) =>
    Math.min(images, Math.floor(nonNegative(value ?? 0)));
  const textItems = Math.floor(nonNegative(input.textItemsPerMonth));
  const textItemTokens =
    textItems * getTextClassificationBillableTokens(nonNegative(input.averageTextTokens));
  const ocrTextTokens = ocrImages * MINIMUM_TEXT_CLASSIFICATION_BILLABLE_TOKENS;

  const quantities: Record<(typeof PUBLIC_PRICED_METERS)[number], number> = {
    image_classification: images,
    text_classification_tokens: textItemTokens + ocrTextTokens,
    ocr_classification: ocrImages,
    person_detection: featureImages(input.personDetectionImagesPerMonth),
    face_detection: featureImages(input.faceDetectionImagesPerMonth),
    asset_storage_mb_hours: nonNegative(input.storedGb) * MB_HOURS_PER_GB_MONTH,
    asset_search: Math.floor(nonNegative(input.assetSearchesPerMonth ?? 0)),
    asset_media_search_indexing: Math.ceil(
      nonNegative(input.mediaSearchIndexingUnitsPerMonth ?? 0),
    ),
    asset_read: Math.floor(nonNegative(input.cdnReadsPerMonth)),
    image_transformed: Math.floor(nonNegative(input.imageTransformsPerMonth)),
    website_ingestion: 0,
  };

  const lines = PUBLIC_PRICED_METERS.filter((meter) => meter !== "website_ingestion").map(
    (meter): MonthlyCostLine => {
      const quantity = quantities[meter];
      const result = calculateTieredPricing(quantity, getMeterRateTiers(meter, prices));
      const freeQuantity = result.tiers
        .filter((tier) => tier.rate === 0)
        .reduce((total, tier) => total + tier.usage, 0);
      return { meter, quantity, freeQuantity, cost: result.totalCost };
    },
  );

  return {
    currency: getBillingCurrency(prices),
    lines,
    total: lines.reduce((total, line) => total + line.cost, 0),
    textItemTokens,
    ocrTextTokens,
  };
};
